import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHttpClient, useErrorMonitor } from '@wix/fe-essentials-standalone';
import {
  useProfileContext,
  ACTIONS,
} from '@components/Contexts/ProfileContext';
import { ClientProject, ReviewData } from '@common/types';
import {
  getReviewDataFromProjects,
  getServicesFromCategories,
} from '../ProjectsReviewsUtils';
import { UpdateProjectResponse } from '@wix/ambassador-partners-marketplace-v1-project/types';
import { PROFILE_REVIEWS_PER_PAGE } from '@utils/constants';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';

export const useProjectsReviewsLogic = () => {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const { logProfilePageClick } = useProfilePageBi();
  const { services, isMyProfile, dispatch, partnerProfile } =
    useProfileContext();

  const [reviews, setReviews] = useState<ReviewData[]>(
    getReviewDataFromProjects(partnerProfile.projects),
  );
  const [isLoadingNewReviews, setIsLoadingNewReviews] =
    useState<boolean>(false);
  const projectReviewsPageCount = useMemo(() => {
    const reviewsCount = partnerProfile?.providerRating?.reviewsCount ?? 0;
    return reviewsCount > 0
      ? Math.ceil(reviewsCount / PROFILE_REVIEWS_PER_PAGE)
      : 0;
  }, [partnerProfile?.providerRating?.reviewsCount]);
  const [currentPage, setCurrentPage] = useState(1);

  const serviceList = useMemo(
    () => getServicesFromCategories(services),
    [services],
  );

  // API calls
  const fetchReviews = useCallback(
    async (page: number) => {
      setIsLoadingNewReviews(true);
      try {
        const { data } = await httpClient.post<{
          projects: Array<ClientProject>;
          projectsCursor?: string;
        }>('/api/partnerProjects', {
          partnerProfileId: partnerProfile.id,
          pagination: { page, limit: PROFILE_REVIEWS_PER_PAGE },
        });
        dispatch({ type: ACTIONS.RESET_PROJECTS, ...data });
      } catch (e) {
        errorMonitor.captureMessage(`Error @fetchPartnerProjects`, {
          contexts: {
            data: {
              partnerProfileId: partnerProfile.id,
              url: window.location.href,
            },
            error: { e },
          },
        });
      } finally {
        setIsLoadingNewReviews(false);
      }
    },
    [httpClient, partnerProfile.id, dispatch, errorMonitor],
  );

  const fetchNextPartnerProjectsPage = useCallback(
    async (page: number) => await fetchReviews(page),
    [fetchReviews],
  );

  const writeReplyToReview = useCallback(
    async (id: string, replyBody: string, revision?: string | null) => {
      const writeReplyReviewResponse =
        await httpClient.post<UpdateProjectResponse>('/api/writeReplyReview', {
          reply: replyBody,
          id,
          revision,
        });
      const updatedProject = writeReplyReviewResponse.data.project;
      dispatch({ type: ACTIONS.UPDATE_PROJECT, project: updatedProject });
    },
    [dispatch, httpClient],
  );

  const infiniteScrollFetchNextReviews = useCallback(async () => {
    try {
      const { data } = await httpClient.post<any>('/api/partnerProjects', {
        partnerProfileId: partnerProfile.id,
        cursor: partnerProfile.projectsCursor,
      });
      dispatch({ type: ACTIONS.SET_PROJECTS, ...data });
    } catch (e) {
      errorMonitor.captureMessage(`Error @fetchNextPartnerProjectsPage`, {
        contexts: {
          data: {
            partnerProfileId: partnerProfile.id,
            url: window.location.href,
          },
          error: { e },
        },
      });
    }
  }, [dispatch, errorMonitor, httpClient, partnerProfile]);

  const logPaginationEvent = () =>
    logProfilePageClick('Reviews navigate between pages');

  useEffect(() => {
    setReviews(getReviewDataFromProjects(partnerProfile.projects));
  }, [partnerProfile]);

  useEffect(() => {
    (async () => {
      await fetchReviews(currentPage);
    })();
  }, [fetchReviews, currentPage]);

  return {
    reviews,
    isLoadingNewReviews,
    projectReviewsPageCount,
    currentPage,
    serviceList,
    isMyProfile,
    partnerProfile,

    setCurrentPage,
    fetchNextPartnerProjectsPage,
    logPaginationEvent,
    writeReplyToReview,
    infiniteScrollFetchNextReviews,
    cursor: partnerProfile.projectsCursor,
  };
};
